import { useState } from "react";
import type { FormEvent } from "react";

import { useMe } from "../api/auth";
import { useMfaPolicy, useMfaPolicySignIn, useUpdateMfaPolicy } from "../api/mfaPolicy";
import { isAmbiguous, isSignedOut, Refused } from "../api/refusal";
import { Loaded, WriteFailed } from "../components/Loaded";
import own from "./MfaPolicy.module.css";
import styles from "./Screen.module.css";

const ROLE_LABELS = { admin: "Admins", approver: "Approvers" };

export function MfaPolicySettings() {
  const me = useMe();
  const update = useUpdateMfaPolicy();
  const signIn = useMfaPolicySignIn();
  const reauthentication = update.data?.reauthentication_required === true;
  const policy = useMfaPolicy(me.data?.role === "admin" && !reauthentication);
  const [draft, setDraft] = useState<boolean | null>(null);
  const [confirming, setConfirming] = useState(false);

  if (me.data?.role !== "admin") {
    return <p className={styles.note}>An Admin manages this workspace's MFA policy.</p>;
  }

  if (reauthentication) {
    return (
      <div className={own.form} data-testid="mfa-policy-signed-out">
        <p className={own.saved} role="status">
          MFA is now required for everyone in this workspace. All live sessions here have ended,
          including yours. Sessions in other workspaces are unchanged.
        </p>
        <button type="button" className={styles.primary} onClick={signIn}>
          Sign in again
        </button>
      </div>
    );
  }

  if (isSignedOut(policy.error) || isSignedOut(update.error)) {
    return (
      <div className={own.form}>
        <p className={styles.note} role="alert">
          Your session has ended. Sign in again to check this workspace's MFA policy.
        </p>
        <button type="button" className={styles.primary} onClick={signIn}>
          Sign in again
        </button>
      </div>
    );
  }

  function reload() {
    setDraft(null);
    setConfirming(false);
    update.reset();
    void policy.refetch();
  }

  return (
    <Loaded query={policy} of="This workspace's MFA policy">
      {(current) => {
        const selected = draft ?? current.require_mfa_for_all_members;
        const changed = selected !== current.require_mfa_for_all_members;
        const enabling = changed && selected;
        const uncertain = isAmbiguous(update.error);
        const forbidden = update.error instanceof Refused && update.error.isUnauthorized;
        const disabled = update.isPending || uncertain || forbidden;

        function submit(event: FormEvent) {
          event.preventDefault();
          if (!changed || disabled) return;
          if (enabling && !confirming) {
            setConfirming(true);
            return;
          }
          update.mutate(selected, {
            onSuccess: () => {
              setDraft(null);
              setConfirming(false);
            },
          });
        }

        return (
          <form className={own.form} onSubmit={submit} data-testid="mfa-policy-form">
            <p className={styles.note}>
              {current.mandatory_roles.map((role) => ROLE_LABELS[role]).join(" and ")} always require
              MFA. This requirement cannot be turned off.
            </p>
            <label className={own.check}>
              <input
                type="checkbox"
                checked={selected}
                disabled={disabled || confirming}
                aria-describedby="mfa-policy-scope"
                onChange={(event) => {
                  setDraft(event.target.checked);
                  setConfirming(false);
                  update.reset();
                }}
              />
              Require MFA for every workspace member
            </label>
            <p className={styles.note} id="mfa-policy-scope">
              Applies to this workspace only. Members who have not set up MFA will enrol when they
              next sign in.
            </p>
            {confirming ? (
              <div className={own.confirm} role="group" aria-label="Confirm MFA requirement">
                <p className={styles.note}>
                  Enabling this signs everyone out of this workspace, including you. Everyone must
                  sign in again and complete MFA. Other workspaces are unaffected.
                </p>
                <div className={own.actions}>
                  <button type="submit" className={styles.primary} disabled={disabled}>
                    {update.isPending ? "Saving…" : "Enable MFA and sign everyone out"}
                  </button>
                  <button
                    type="button"
                    className={styles.fixLink}
                    disabled={update.isPending}
                    onClick={() => setConfirming(false)}
                  >
                    Not now
                  </button>
                </div>
              </div>
            ) : (
              <button type="submit" className={styles.primary} disabled={!changed || disabled}>
                {update.isPending ? "Saving…" : enabling ? "Review MFA change" : "Save MFA policy"}
              </button>
            )}
            {!selected && current.require_mfa_for_all_members ? (
              <p className={styles.note}>
                Admins and Approvers will still require MFA. Ending the requirement for other roles
                does not restore sessions that have already ended.
              </p>
            ) : null}
            {update.isSuccess ? (
              <p className={own.saved} role="status">
                MFA policy saved.
              </p>
            ) : null}
            <WriteFailed error={update.error} of="this workspace's MFA policy" onReload={reload} />
            {uncertain ? (
              <button type="button" className={styles.fixLink} onClick={signIn}>
                Sign in again to check the policy
              </button>
            ) : null}
          </form>
        );
      }}
    </Loaded>
  );
}
